import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { observer, inject } from 'mobx-react'
import { Dialog, Button, Intent } from '@blueprintjs/core'

import documentSync from '@client/utils/DocumentSync'
import { allowLeaving } from '@client/utils/pageUnload'
import { showRemoteChangeToast } from '@client/components/RemoteChangeToast/model'

/**
 * Shown when a save was blocked because the server has a newer version than
 * the one this tab opened (EditorModel.findSaveConflict). The only way on is
 * loading that version; until then nothing saves. See FE-1441.
 *
 * There's no "keep my changes": the user can't compare the two versions, and
 * autosave runs every minute, so loading the newer one loses at most about a
 * minute of edits, while overwriting it could lose much more.
 *
 * A dialog rather than a banner: on release 175 the editor's banners sit
 * behind the toolbar (FE-1433).
 */
export const SaveConflictDialog = ({ app, editor }) => {
  const conflict = editor.saveConflict

  useEffect(() => {
    if (conflict) {
      documentSync.countSaveConflict({ event: 'editorSaveBlocked' })
    }
  }, [conflict])

  if (!conflict) {
    return null
  }

  const loadNewer = () => {
    documentSync.countSaveConflict({
      event: 'editorSaveConflictResolved',
      choice: 'reload',
    })
    // reloadFromServer() clears the conflict once the newer version is in.
    if (documentSync.adopt(conflict)) {
      showRemoteChangeToast({
        messageKey: 'saveConflict.loadedNewer',
        warning: true,
      })
      return
    }
    // The swap refuses a body it can't take in place (no settings or no
    // sections). The dialog can't be dismissed, so without this the tab would
    // be stuck. A page load opens it the normal way; the conflict stops this
    // tab's own save on the way out. The user chose to drop their edits, so the
    // browser shouldn't ask again (FE-1454).
    allowLeaving()
    window.location.reload()
  }

  return (
    <Dialog
      className="save-conflict-dialog"
      icon="warning-sign"
      title={app.formatMessage('saveConflict.title')}
      isOpen
      isCloseButtonShown={false}
      canOutsideClickClose={false}
      canEscapeKeyClose={false}
    >
      <div className="bp6-dialog-body">
        <p>{app.formatMessage('saveConflict.description')}</p>
      </div>
      <div className="bp6-dialog-footer">
        <div className="bp6-dialog-footer-actions">
          <Button intent={Intent.PRIMARY} onClick={loadNewer}>
            {app.formatMessage('saveConflict.loadNewer')}
          </Button>
        </div>
      </div>
    </Dialog>
  )
}

SaveConflictDialog.propTypes = {
  app: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }).isRequired,
  editor: PropTypes.shape({
    saveConflict: PropTypes.object,
  }).isRequired,
}

export default inject('app', 'editor')(observer(SaveConflictDialog))
